import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Download as DownloadIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';

export interface PatientDocumentRow {
  id: string;
  title?: string | null;
  file_url: string;
}

interface PatientDocumentActionsProps {
  doc: PatientDocumentRow;
  patientId: string;
  onUpdated: (doc: PatientDocumentRow & Record<string, unknown>) => void;
  onDeleted: (docId: string) => void;
  onNotify: (message: string, severity?: 'success' | 'error') => void;
  iconSize?: 'small' | 'medium';
}

export default function PatientDocumentActions({
  doc,
  patientId,
  onUpdated,
  onDeleted,
  onNotify,
  iconSize = 'medium',
}: PatientDocumentActionsProps) {
  const [renameOpen, setRenameOpen] = useState(false);
  const [title, setTitle] = useState(doc.title || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (renameOpen) setTitle(doc.title || '');
  }, [renameOpen, doc.title]);

  const handleRename = async () => {
    const nextTitle = title.trim();
    if (!nextTitle) {
      onNotify('Informe um nome para o documento.', 'error');
      return;
    }
    if (nextTitle === (doc.title || '').trim()) {
      setRenameOpen(false);
      return;
    }

    setSaving(true);
    try {
      const { data: res } = await axios.patch(
        getApiUrl(`/clinic/patients/${patientId}/documents/${doc.id}`),
        { title: nextTitle },
      );
      if (res.success) {
        onUpdated(res.data);
        setRenameOpen(false);
        onNotify('Nome do documento atualizado.', 'success');
      } else {
        onNotify(res.error || 'Erro ao renomear documento.', 'error');
      }
    } catch (err: unknown) {
      const message =
        axios.isAxiosError(err) && err.response?.data?.error
          ? String(err.response.data.error)
          : 'Erro ao renomear documento.';
      onNotify(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Excluir o documento "${doc.title || 'documento'}"? Esta ação não pode ser desfeita.`)) {
      return;
    }
    try {
      const { data: res } = await axios.delete(
        getApiUrl(`/clinic/patients/${patientId}/documents/${doc.id}`),
      );
      if (res.success) {
        onDeleted(doc.id);
        onNotify('Documento excluído com sucesso!', 'success');
      } else {
        onNotify(res.error || 'Erro ao excluir documento', 'error');
      }
    } catch (err: unknown) {
      const message =
        axios.isAxiosError(err) && err.response?.data?.error
          ? String(err.response.data.error)
          : 'Erro ao excluir documento.';
      onNotify(message, 'error');
    }
  };

  const btnSx =
    iconSize === 'small'
      ? { p: 0.5, '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }
      : undefined;

  return (
    <>
      <Tooltip title="Renomear">
        <IconButton size={iconSize} onClick={() => setRenameOpen(true)} sx={{ color: '#64748b', ...btnSx }}>
          <EditIcon sx={{ fontSize: iconSize === 'small' ? 18 : 22 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Baixar / Visualizar">
        <IconButton
          size={iconSize}
          onClick={() => window.open(doc.file_url, '_blank')}
          sx={{ color: '#2563eb', ...(iconSize === 'small' ? { '&:hover': { bgcolor: 'rgba(37,99,235,0.08)' } } : undefined) }}
        >
          <DownloadIcon sx={{ fontSize: iconSize === 'small' ? 18 : 22 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Excluir">
        <IconButton
          size={iconSize}
          onClick={() => void handleDelete()}
          sx={{ color: '#dc2626', ...(iconSize === 'small' ? { '&:hover': { bgcolor: 'rgba(220,38,38,0.08)' } } : undefined) }}
        >
          <DeleteIcon sx={{ fontSize: iconSize === 'small' ? 18 : 22 }} />
        </IconButton>
      </Tooltip>

      <Dialog open={renameOpen} onClose={() => !saving && setRenameOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Renomear documento</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Nome do documento"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleRename();
            }}
            disabled={saving}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRenameOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void handleRename()} disabled={saving}>
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
